







































































































































































































































































import { defineComponent, ref, useRoute, useFetch, computed, useContext, onMounted } from '@nuxtjs/composition-api';
import dayjs from 'dayjs';
import EventDetailReservationButton from '~/components/pages/Event/Detail/EventDetailReservationButton.vue';
import EventCard from '~/components/pages/Event/EventCard.vue';
import ProductWineColorLabel from '~/components/pages/Product/ProductWineColorLabel.vue';
import AppIcon from '~/components/partials/App/AppIcon.vue';
import AppImage from '~/components/partials/App/AppImage.vue';
import AppLink from '~/components/partials/App/AppLink.vue';
import AppLoader from '~/components/partials/App/AppLoader.vue';
import AppPrice from '~/components/partials/App/AppPrice.vue';
import AppRectButton from '~/components/partials/App/AppRectButton.vue';
import AppRelatedContents from '~/components/partials/App/AppRelatedContents.vue';
import ContentTagsAndShareIcons from '~/components/partials/Content/ContentTagsAndShareIcons.vue';
import GlobalHeaderMenu from '~/components/partials/Global/GlobalHeaderMenu.vue';
import LayoutDefault from '~/components/partials/Layout/LayoutDefault.vue';
import ProducingAreaFlag from '~/components/partials/ProducingArea/ProducingAreaFlag.vue';
import ProductLabel from '~/components/partials/Product/ProductLabel.vue';
import { useTags } from '~/composables/contents/useTags';
import { useSilverEggApi } from '~/composables/silverEgg/useSilverEggApi';
import { useUuidEncryption } from '~/composables/silverEgg/useUuidEncryption';
import { useLayoutProps } from '~/composables/useLayoutProps';
import { useStore } from '~/composables/useStore';
import { CATEGORY_ID, REQUEST_NUM, SMARTPHONE, SPEC } from '~/constants';
import { BOTTLE_200 } from '~/constants/profilesForImage';
import { url, nonNullable } from '~/utils';
import { PageError } from '~/utils/error';
import { formatCategory, getCancellablePeriod } from '~/utils/event';
import { ECSchema } from '~/types/api';
import { Tags } from '~/types/modules/contents';
import {
  EventDetailResponseSchema,
  EventDetailWineListItem,
  EventSummaryResponseSchema,
  EventCardTags,
} from '~/types/modules/event';
import ActionMail from '~/assets/svg/icon_action-mail.svg?inline';
import ActionTel from '~/assets/svg/icon_action-tel.svg?inline';
import { SeatsLeft, EventStatus, EventCategoryKeys } from '~/enums/event';

/** ワインリストの表示切り替えの閾値 */
const WINE_LIST_LENGTH_BORDER = 10;
/** 他のイベントの表示上限 */
const DISPLAYED_OTHER_EVENT_LIMIT = 6;
// 特価とヴィンテージ情報を子商品側から追加で取得する
interface EventDetailWineListItemWithSpecialPriceAndVintage extends EventDetailWineListItem {
  specialPrice: number;
  vintage: string;
  displayFlag: boolean;
}

export default defineComponent({
  components: {
    AppIcon,
    AppImage,
    AppLink,
    AppLoader,
    AppPrice,
    AppRectButton,
    ContentTagsAndShareIcons,
    EventCard,
    EventDetailReservationButton,
    LayoutDefault,
    ProductWineColorLabel,
    ProducingAreaFlag,
    ProductLabel,
    AppRelatedContents,
    ActionMail,
    ActionTel,
    GlobalHeaderMenu,
  },
  setup() {
    const route = useRoute();
    const store = useStore();
    const { i18n, app } = useContext();
    const { mappedTags, allTagNames, findTagsByEventId } = useTags();
    const { fetchRecommendItems } = useSilverEggApi();
    const { encryptUuid } = useUuidEncryption();
    const eventDetail = ref<EventDetailResponseSchema>();
    const wineList = ref<EventDetailWineListItemWithSpecialPriceAndVintage[]>([]);
    const sameAreaEvents = ref<EventSummaryResponseSchema[]>([]);
    const sameAreaEventsTags = ref<EventCardTags[]>([]);
    const tags = ref<Tags>([]);
    const uuidHash = ref<string | undefined>('');

    const eventId = computed(() => Number(route.value.params.id));
    const isCanceled = computed(() => eventDetail.value?.status === EventStatus.CANCELED);
    const isMobile = computed(() => app.$ua.deviceType() === SMARTPHONE);

    const formPath = computed(() => {
      return url('EVENT_RESERVE_DATE', { id: eventId.value });
    });

    const cancelFormPath = computed(() => {
      return url('EVENT_WAITING_FORM', { id: eventId.value });
    });

    const shopDetail = ref<ECSchema<'saleslocation.ShowResponse'>>();

    /** 予約可能な日時が存在するか */
    const hasSeatsLeft = computed(
      () =>
        eventDetail.value?.slots?.some(
          ({ seatsLeft }) => seatsLeft && [SeatsLeft.MANY, SeatsLeft.FEW].includes(seatsLeft as SeatsLeft)
        ) || false
    );

    /** キャンセル待ちで予約可能な日時が存在するか */
    const hasWaitingSeatsLeft = computed(
      () =>
        eventDetail.value?.slots?.some(({ seatsLeft }) => seatsLeft && SeatsLeft.WAITING_LIST === seatsLeft) || false
    );

    /**
     * 下記条件を全て満たす場合、キャンセル待ち予約が可能となる
     * ・イベント自体がキャンセルされていない
     * ・キャンセル待ち可能フラグがtrue
     * ・カテゴリがイベント or ディナー・ランチ
     * ・キャンセル待ちで予約可能な日時が存在する
     * ・予約可能な日時が存在しない or 「管理画面で設定した最大予約人数」を超えている
     */
    const hasWaitingList = computed(() => {
      if (
        isCanceled.value ||
        !eventDetail.value?.hasWaitingList ||
        ![EventCategoryKeys.EVENT, EventCategoryKeys.DINNER_LUNCH].includes(
          eventDetail.value?.category as EventCategoryKeys
        ) ||
        !hasWaitingSeatsLeft.value
      ) {
        return false;
      }
      if (!hasSeatsLeft.value || eventDetail.value?.isExceedingMaxCapacity) {
        return true;
      }
      return false;
    });

    const formattedSlots = computed(() => {
      return (
        eventDetail.value?.foldedSlots?.map((slot) => {
          const { date, start, end, seatsLeft } = slot;
          const startDate = dayjs(`${date}T${start}`);
          const endDate = dayjs(`${date}T${end}`);
          return {
            startDate: startDate.isValid() ? startDate.format('MM月DD日（dd）HH:mm') : undefined,
            endDate: endDate.isValid() ? endDate.format('HH:mm') : undefined,
            seatsLeft,
          };
        }) || []
      );
    });

    /** 開催場所のリンク */
    const venueLink = computed(() => {
      if (!eventDetail.value) {
        return null;
      }
      const { venueUrl, shopPermalink } = eventDetail.value;
      const shopPath = shopPermalink ? url('SHOP_DETAIL', { permalink: shopPermalink }) : null;
      return venueUrl || shopPath;
    });

    /** クレジットカード決済か */
    const isCreditCardPayment = computed<boolean>(() => eventDetail.value?.creditCardPayment || false);

    const { fetchState } = useFetch(async () => {
      try {
        eventDetail.value = await store.dispatch('event/detail/fetchEventDetail', { path: { eventId: eventId.value } });
      } catch {
        throw new PageError(404, {});
      }
      try {
        const productCodes = eventDetail.value?.wineList?.map(({ parentCode }) => parentCode) || [];
        if (productCodes.length > 0) {
          const { items } = await store.dispatch('modules/product/fetchProducts', {
            q: `product_code_s:(${productCodes.join(' OR ')})`,
          });
          const sortedWineList = eventDetail.value?.wineList?.sort((a, b) => a.displayOrder - b.displayOrder);
          wineList.value =
            sortedWineList
              ?.map(({ parentCode, notForSale, childCode }) => {
                const product = items.find((item) => item.code === parentCode);
                if (!product) {
                  return null;
                }
                const {
                  code: productCode,
                  image,
                  color,
                  name,
                  size,
                  size_unit,
                  producer,
                  producing_area,
                  children,
                  display_flag,
                } = product;
                const path = url('ITEM_DETAIL', { code: productCode });
                // 子商品コードが完全一致の子商品のみ抽出する
                // 生産年_表示価格_新着フラグ_定価_在庫数_販売開始日時_販売終了日時_最高得点の評価誌種別_最高得点の評価誌ポイント_表示フラグ_子商品コード
                const targetChild = children.find((child) => {
                  const childProductCode = child.split('_').splice(10).join('_');
                  return childProductCode === childCode;
                });
                const childProperties = targetChild?.split('_') || [];
                // ヴィンテージ年数を取得
                const vintage = childProperties[0]?.replace('年', '') || '';
                // 定価を取得
                const price = Number(childProperties[3]?.replace('円', '') || 0);
                // 特価を取得
                const specialPrice = Number(childProperties[1]?.replace('円', '') || 0);
                return {
                  path,
                  image,
                  color,
                  name,
                  size: `${size}${size_unit}`,
                  producer,
                  area: {
                    code_id: producing_area.area_id_1 || '',
                    areas: [
                      {
                        name: producing_area.area_1,
                        id: producing_area.area_id_1,
                      },
                      {
                        name: producing_area.area_2,
                        id: producing_area.area_id_2,
                      },
                      {
                        name: producing_area.area_3,
                        id: producing_area.area_id_3,
                      },
                    ],
                  },
                  vintage,
                  price,
                  specialPrice,
                  notForSale: !!notForSale,
                  displayFlag: !!display_flag,
                };
              })
              .filter(nonNullable) || [];
        }
      } catch {
        // 何もしない
      }
      try {
        if (eventDetail.value?.venueAreaId) {
          // 同じエリア＆現在時刻以降のイベント一覧を取得
          const { items } = await store.dispatch('event/list/fetchEvents', {
            query: {
              dateFrom: dayjs().format('YYYY-MM-DD'),
              area: [eventDetail.value.venueAreaId],
              sort: 'date',
              availableOnly: true,
            },
          });

          const trimmedItems =
            items?.filter(({ id }) => id !== eventId.value).slice(0, DISPLAYED_OTHER_EVENT_LIMIT) || [];

          const sameCategoryItems = trimmedItems.filter((item) => eventDetail.value?.category === item.category) || [];
          const otherItems = trimmedItems.filter((item) => eventDetail.value?.category !== item.category) || [];

          sameAreaEvents.value = sameCategoryItems.concat(otherItems);
          sameAreaEventsTags.value = await allTagNames(sameAreaEvents.value);
        }
      } catch {
        // 何もしない
      }

      tags.value = (await mappedTags([], eventDetail.value?.tags)) || [];
    });

    onMounted(async () => {
      // シルバーエッグのレコメンド枠リクエスト&トラッキング
      if (app.$ua.isFromCrawler()) return;
      uuidHash.value = await encryptUuid();
      await fetchRecommendItems({
        prod: ['e' + eventId.value], // トラッキング用
        spec: isMobile.value ? SPEC.ARTICLE_DETAIL_SP : SPEC.ARTICLE_DETAIL_PC, // 読み物と共用
        num: REQUEST_NUM,
        cat: CATEGORY_ID.EVENT,
        cust: uuidHash.value,
      });
    });

    useLayoutProps(() => {
      const title = eventDetail.value?.title || i18n.t('Event-02.unknown.event.title').toString();
      return {
        layout: {
          header: 'always',
          headerMenu: 'never',
          footer: 'always',
          footerMenu: 'always',
          topBanner: 'never',
        },
        head: {
          title,
          description: i18n.t('Event-02.description').toString(),
          // イベント一覧表示フラグがfalseのものはnoindexを設定
          noindex: !eventDetail.value?.listViewFlag,
        },
        og: {
          image: eventDetail.value?.mainImageUrl,
        },
        breadcrumbs: [
          { title: i18n.t('Other-02.pageTitle').toString(), to: { path: url('SHOP_LIST') } },
          { title: i18n.t('Event-01.pageTitle').toString(), to: { path: url('EVENT_LIST') } },
          { title: eventDetail.value?.title || '', to: null },
        ],
        fetchState,
      };
    });

    // キャンセル可能期間
    const cancellablePeriod = computed(() => {
      if (!eventDetail.value) return;

      return getCancellablePeriod(eventDetail.value?.category);
    });

    return {
      cancellablePeriod,
      eventDetail,
      wineList,
      sameAreaEvents,
      sameAreaEventsTags,
      findTagsByEventId,
      tags,
      shopDetail,
      isCanceled,
      formPath,
      cancelFormPath,
      hasSeatsLeft,
      hasWaitingList,
      formattedSlots,
      venueLink,
      fetchState,
      url,
      SeatsLeft,
      formatCategory,
      isCreditCardPayment,
      eventId,
      WINE_LIST_LENGTH_BORDER,
      DISPLAYED_OTHER_EVENT_LIMIT,
      BOTTLE_200,
      EventCategoryKeys,
    };
  },
});
